import { Dispatch } from 'redux'
import { sendTrackingRequestAction } from 'client/contexts/Tracking/actions'
import {
  checkoutIntent,
  checkoutError,
  checkoutErrorFrom200,
} from 'client/contexts/Tracking/events'
import {
  mapConfirmationErrorMessage,
  mapPaymentAliasErrorMessage,
} from 'client/contexts/Tracking/serverSideErrorMapper'
import { ERROR_CODES } from 'client/pages/constants'
import Cookies from 'js-cookie'
import { report } from 'src/utils/clientErrorReporter'
import { PURCHASE_REMINDER_COOKIE } from 'src/comps/Notifications/PurchaseReminder/constants'
import { PaymentMessages } from 'src/comps/PaymentForm/utils'
import { ErrorHandlingResult } from '../utils'

interface ErrorResponse {
  response?: {
    data?: {
      error?: string
    }
    status?: number
  }
  code?: number
  message?: string
}

interface HandleExitParams {
  error: ErrorResponse | Error | string
  redirectToHotelDetail: () => void
}

function handleExit({ error, redirectToHotelDetail }: HandleExitParams) {
  report(error as Error)
  redirectToHotelDetail()
  Cookies.remove(PURCHASE_REMINDER_COOKIE)
}

interface HandleReloadParams {
  error: ErrorResponse | number | null
  refreshPaymentData: () => void
}

function handleReload({ error, refreshPaymentData }: HandleReloadParams) {
  report(error as Error)
  refreshPaymentData()
}

interface HandlePaymentMethodErrorParams {
  error: string
  dispatch: Dispatch
  purchaseId: string
}

export function handlePaymentMethodError({
  error,
  dispatch,
  purchaseId,
}: HandlePaymentMethodErrorParams): void {
  dispatch(
    sendTrackingRequestAction({
      event: checkoutIntent({
        reason: 'payment_methods_provider',
        context: 'payment_checkout',
        status: 'KO',
        purchaseId,
        details: `Error in payment methods provider: ${error}`,
      }),
    })
  )
}

interface HandleCreateAliasErrorParams {
  error: ErrorResponse
  dispatch: Dispatch
  purchaseId: string
  redirectToHotelDetail: () => void
}

export function handleCreateAliasError({
  error,
  dispatch,
  purchaseId,
  redirectToHotelDetail,
}: HandleCreateAliasErrorParams): boolean {
  dispatch(
    sendTrackingRequestAction({
      event: checkoutIntent({
        reason: mapPaymentAliasErrorMessage(
          error.response?.data?.error ?? (error.response?.data as string),
          error.response?.status ?? 0
        ),
        context: 'payment_checkout',
        status: 'KO',
        purchaseId,
        details: 'Error creating alias',
      }),
    })
  )
  handleExit({ error, redirectToHotelDetail })
  return true
}

interface HandleInternalErrorParams {
  code: number | string
  error: string
  dispatch: Dispatch
  purchaseId: string
  redirectToHotelDetail: () => void
  status: number
}

export function handleInternalError({
  code,
  error,
  dispatch,
  purchaseId,
  redirectToHotelDetail,
  status,
}: HandleInternalErrorParams): boolean {
  if (code === ERROR_CODES.INTERNAL_ERROR && error) {
    dispatch(
      sendTrackingRequestAction({
        event: checkoutIntent({
          reason: mapConfirmationErrorMessage(error ?? '', status ?? 0),
          context: 'payment_checkout',
          status: 'KO',
          purchaseId,
          details: `Error in getConfirmation: ${error}`,
        }),
      })
    )
    handleExit({ error, redirectToHotelDetail })
    return true
  }
  return false
}

interface HandlePaymentProviderErrorParams {
  code: number | string
  subCode: number | null
  dispatch: Dispatch
  activatePaymentError: () => void
  refreshPaymentData: () => void
  setSubCodeError: (subCode: number | null) => void
  purchaseId: string
}

export function handlePaymentProviderError({
  code,
  subCode,
  dispatch,
  activatePaymentError,
  refreshPaymentData,
  setSubCodeError,
  purchaseId,
}: HandlePaymentProviderErrorParams): boolean {
  if (code === ERROR_CODES.PAYMENT_PROVIDER && subCode === ERROR_CODES.INVALID_CARD) {
    dispatch(
      sendTrackingRequestAction({
        event: checkoutIntent({
          reason: 'confirmation_payment',
          context: 'payment_checkout',
          status: 'KO',
          purchaseId,
          details: 'Error: PAYMENT_PROVIDER and INVALID_CARD',
        }),
      })
    )
    activatePaymentError()
    setSubCodeError(ERROR_CODES.INVALID_CARD)
    handleReload({ error: subCode, refreshPaymentData })
    return true
  }

  if (code === ERROR_CODES.PAYMENT_PROVIDER || code === ERROR_CODES.CONFIRMATION_PAYMENT) {
    dispatch(
      sendTrackingRequestAction({
        event: checkoutIntent({
          reason: 'confirmation_payment',
          context: 'payment_checkout',
          status: 'KO',
          purchaseId,
          details: 'Error: PAYMENT_PROVIDER or CONFIRMATION_PAYMENT',
        }),
      })
    )
    activatePaymentError()
    setSubCodeError(subCode ?? null)
    handleReload({ error: subCode, refreshPaymentData })
    return true
  }

  return false
}

interface HandleConfirmationErrorParams {
  code: number | string | undefined
  error: string
  dispatch: Dispatch
  redirectToHotelDetail: () => void
  purchaseId: string
  status: number
}

export function handleConfirmationError({
  code,
  error,
  dispatch,
  redirectToHotelDetail,
  purchaseId,
  status,
}: HandleConfirmationErrorParams): boolean {
  const confirmationErrorCodes = [
    ERROR_CODES.CONFIRMATION_BAD_USER,
    ERROR_CODES.CONFIRMATION_NOT_PAYABLE,
    ERROR_CODES.CONFIRMATION_PURCHASE_LOCKED,
    ERROR_CODES.CONFIRMATION_PURCHASE_ID_NOT_FOUND,
    ERROR_CODES.CONFIRMATION_RULES,
  ]

  if ((code === undefined || code === null) && error && status) {
    dispatch(
      sendTrackingRequestAction({
        event: checkoutIntent({
          reason: mapConfirmationErrorMessage(error, status),
          context: 'payment_checkout',
          status: 'KO',
          purchaseId,
          details: `Error in getConfirmation: ${error}`,
        }),
      })
    )
    handleExit({ error, redirectToHotelDetail })
    return true
  }

  if (confirmationErrorCodes.includes(code as ERROR_CODES)) {
    dispatch(
      sendTrackingRequestAction({ event: checkoutErrorFrom200({ response: { code, error } }) })
    )

    const errorMessages: { [key: string]: string } = {
      [ERROR_CODES.CONFIRMATION_BAD_USER]: 'CONFIRMATION_BAD_USER',
      [ERROR_CODES.CONFIRMATION_NOT_PAYABLE]: 'CONFIRMATION_PAYMENT',
      [ERROR_CODES.CONFIRMATION_PURCHASE_LOCKED]: 'CONFIRMATION_PURCHASE_LOCKED',
      [ERROR_CODES.CONFIRMATION_PURCHASE_ID_NOT_FOUND]: 'CONFIRMATION_PURCHASE_ID_NOT_FOUND',
      [ERROR_CODES.CONFIRMATION_RULES]: 'CONFIRMATION_BOOKING_RULES_CONFIRMATION',
    }

    const errorReason = errorMessages[code ?? ''] || 'unknown_error'
    const errorDetail = error
      ? `Error in getConfirmation: ${error}`
      : (errorMessages[code ?? ''] ?? 'Unknown error')

    dispatch(
      sendTrackingRequestAction({
        event: checkoutIntent({
          reason: errorReason.toLowerCase(),
          context: 'payment_checkout',
          status: 'KO',
          purchaseId,
          details: errorDetail,
        }),
      })
    )
    redirectToHotelDetail()
    return true
  }

  if (code === 'ECONNABORTED') {
    dispatch(
      sendTrackingRequestAction({
        event: checkoutIntent({
          reason: 'confirmation_timeout',
          context: 'payment_checkout',
          status: 'KO',
          purchaseId,
          details: `Timeout in getConfirmation: ${error}`,
        }),
      })
    )
    redirectToHotelDetail()
    return true
  }

  return false
}

interface HandleGeneralErrorParams {
  error: ErrorResponse
  dispatch: Dispatch
  redirectToHotelDetail: () => void
  purchaseId: string
}

export function handleGeneralError({
  error,
  dispatch,
  redirectToHotelDetail,
  purchaseId,
}: HandleGeneralErrorParams): void {
  dispatch(sendTrackingRequestAction({ event: checkoutError({ error }) }))
  dispatch(
    sendTrackingRequestAction({
      event: checkoutIntent({
        reason: 'confirmation_unhandled_error',
        context: 'payment_checkout',
        status: 'KO',
        purchaseId,
        details: error.message ?? 'Unknown error',
      }),
    })
  )
  handleExit({ error, redirectToHotelDetail })
}

interface HandleSuccessParams {
  dispatch: Dispatch
  purchaseId: string
  type?: PaymentMessages
}

export function handleSuccess({ dispatch, purchaseId, type }: HandleSuccessParams): void {
  dispatch(
    sendTrackingRequestAction({
      event: checkoutIntent({
        reason:
          type === PaymentMessages.VALIDATION_3DS ? 'confirmation_3ds_payment' : 'payment_success',
        context: 'payment_checkout',
        status: 'OK',
        purchaseId,
        details: '',
      }),
    })
  )
}

interface HandleIframeErrorParams {
  error: ErrorHandlingResult
  dispatch: Dispatch
  purchaseId: string
}

export const handleIframeError = ({
  error,
  dispatch,
  purchaseId,
}: HandleIframeErrorParams): void => {
  dispatch(
    sendTrackingRequestAction({
      event: checkoutIntent({
        eventName: error.eventName ?? 'purchase_attempt',
        reason: error.reason,
        context: error.context,
        status: error.status,
        purchaseId,
        details: error.details,
      }),
    })
  )
}
